import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Eye, EyeOff, Globe2, ArrowRight } from "lucide-react";

const ROLE_REDIRECT = { admin: "/admin", coordinator: "/coordinator", partner: "/partner", patient: "/portal" };

export default function Login() {
  const { login, getApiErrorMessage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@aimshealthcare.in");
  const [password, setPassword] = useState("Admin@123");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      const target = location.state?.from?.pathname || ROLE_REDIRECT[user.role] || "/";
      navigate(target, { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fillCreds = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="min-h-screen flex bg-[#F7F5F2]">
      {/* Left: visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/7789603/pexels-photo-7789603.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2A4D3E] via-[#2A4D3E]/85 to-[#1E3B2F]/90" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <Globe2 size={20} strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold leading-tight">AIMS Healthcare</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">International Patient Management</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D97757] mb-4">For coordinators, partners & patients</div>
            <h2 className="font-heading text-4xl font-semibold leading-tight">
              One platform for the<br />entire international<br />patient journey.
            </h2>
            <p className="mt-5 text-white/80 text-sm leading-relaxed">
              From the first enquiry to discharge and follow-up — track every lead,
              coordinate every partner referral, and run every quotation from a single
              place. Built for global medical tourism teams.
            </p>
          </div>
          <div className="text-[11px] text-white/50">
            © 2026 AIMS Healthcare · aimshealthcare.in
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8E9992] font-semibold mb-3">Sign in</div>
            <h1 className="font-heading text-4xl font-semibold text-[#2A332C] tracking-tight">Welcome back</h1>
            <p className="text-sm text-[#5C6660] mt-2">Continue managing your patient journeys.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-[#5C6660] mb-1.5">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full border border-[#E8E6E1] rounded-xl px-4 py-3 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E] transition"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5C6660] mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full border border-[#E8E6E1] rounded-xl px-4 py-3 pr-11 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E] transition"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E9992] hover:text-[#2A4D3E] transition" data-testid="toggle-password-visibility">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-[#C0392B] bg-[#FDEDEC] border border-[#F5D6D2] rounded-xl px-4 py-3" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full bg-[#2A4D3E] text-white rounded-full px-6 py-3.5 font-medium transition-all hover:bg-[#1E3B2F] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>)}
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-[#5C6660]">
            <Link to="/forgot-password" className="text-[#5C6660] hover:text-[#2A4D3E] hover:underline" data-testid="forgot-link">
              Forgot password?
            </Link>
            <span className="mx-2 text-[#E8E6E1]">·</span>
            New referral partner?{" "}
            <Link to="/register" className="text-[#2A4D3E] font-semibold hover:underline" data-testid="register-link">
              Create an account
            </Link>
          </div>

          <div className="mt-10 pt-6 border-t border-[#E8E6E1]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8E9992] font-semibold mb-3">Demo accounts</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                ["Admin", "admin@aimshealthcare.in", "Admin@123"],
                ["Coordinator", "priya@aimshealthcare.in", "Coord@123"],
                ["Partner", "ahmed@dubaimedconnect.ae", "Partner@123"],
              ].map(([label, em, pw]) => (
                <button
                  key={em} type="button" onClick={() => fillCreds(em, pw)}
                  data-testid={`demo-${label.toLowerCase()}-button`}
                  className="text-left px-3 py-2.5 border border-[#E8E6E1] rounded-xl hover:border-[#2A4D3E] hover:bg-white transition"
                >
                  <div className="font-medium text-[#2A332C]">{label}</div>
                  <div className="text-[10px] text-[#8E9992] truncate">{em}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
